import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S } from '../regureStyles';
import { hasPermission } from '../../config/rbac';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const FALLBACK = [
  { _id: 'doc_01', filename: 'claim_report_march.pdf',  documentType: 'Claim Report',     confidence: 0.94, extractedFields: { claimNumber: 'CL-2026-00142', lossDate: '2026-02-12', amount: 18500 }, uploadedAt: '2026-03-01T08:15:00Z', status: 'unmatched', piiRedacted: true },
  { _id: 'doc_02', filename: 'medical_invoice_042.pdf', documentType: 'Medical Invoice',   confidence: 0.87, extractedFields: { patient: 'A. Müller', invoiceDate: '2026-02-28', total: 3250 },       uploadedAt: '2026-03-01T09:42:00Z', status: 'unmatched', piiRedacted: true },
  { _id: 'doc_03', filename: 'police_report_bern.pdf',  documentType: 'Police Report',     confidence: 0.91, extractedFields: { reportNumber: 'KAN-2026-0894', date: '2026-02-25' },                  uploadedAt: '2026-02-28T14:30:00Z', status: 'unmatched', piiRedacted: false },
  { _id: 'doc_04', filename: 'contract_renewal_v2.pdf', documentType: 'Policy Document',   confidence: 0.79, extractedFields: { policyNumber: 'POL-2026-0123', effectiveDate: '2026-04-01' },          uploadedAt: '2026-02-27T11:00:00Z', status: 'unmatched', piiRedacted: true },
  { _id: 'doc_05', filename: 'witness_statement.pdf',   documentType: 'Witness Statement', confidence: 0.68, extractedFields: {},                                                                      uploadedAt: '2026-02-26T16:20:00Z', status: 'unmatched', piiRedacted: false },
];

const useDocumentFunctions = () => {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [attachedCount, setAttachedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [linkClaimId, setLinkClaimId] = useState('');
  const [linking, setLinking] = useState(false);
  // Attach-to-client state is symmetric with link-to-claim. Whether the row
  // buttons expose one, the other, or both is driven by tenantConfig features
  // — not hardcoded to any specific tenant.
  const [linkClientId, setLinkClientId] = useState('');
  const [clientPickerFor, setClientPickerFor] = useState(null); // FileExchange row being attached
  const [availableClients, setAvailableClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileInputRef = useRef();

  // Feature flags from the current tenant's config. The Document Processing
  // screen's row actions must adapt to which linking targets the tenant has
  // enabled — no tenant-specific carve-outs in this component.
  const features = useSelector(state => state?.tenantConfig?.config?.features) || {};
  const canLinkToClaim  = Boolean(features.claims);
  const canLinkToClient = Boolean(features.clients);

  // Role-based upload gate. Previously the Upload button was always
  // visible — anyone who reached the screen saw it, even when their
  // role didn't hold documents.upload. Reading the same permission map
  // the backend gate uses keeps the FE/BE in sync.
  const userRole = useSelector(state => {
    const loggedIn = state?.loggedinUser?.userInfo?.data;
    const login    = state?.userLogin?.userInfo?.data;
    return loggedIn?.data?.role || login?.data?.role || login?.user?.role || null;
  });
  const canUploadDocs = hasPermission(userRole, 'documents', 'upload');

  // Pagination state — the backend returns { docs, total, page, pages }
  // for /documents/unmatched. Previously the frontend only ever fetched
  // page 1 and rendered whichever rows came back; this exposes controls.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // Load the client catalogue on demand (only when a tenant that has clients
  // opens the Attach-to-Client picker). Cached in state so re-opens are free.
  const loadClientsForPicker = useCallback(async () => {
    if (!canLinkToClient || availableClients.length > 0) return;
    try {
      const resp = await axios.get(`${BASE_URL}/clients?limit=500`, { withCredentials: true });
      const list = resp.data?.data?.data || resp.data?.data || resp.data || [];
      setAvailableClients(Array.isArray(list) ? list : []);
    } catch { /* leave empty; picker will render "no clients found" */ }
  }, [canLinkToClient, availableClients.length]);

  const fetchDocs = useCallback(async () => {
    try {
      const resp = await axios.get(
        `${BASE_URL}/documents/unmatched?page=${page}&limit=${pageSize}`,
        { withCredentials: true },
      );
      // Response envelope: { status, results, data: [...], pagination: { total, page, pages } }
      // Older shape also handled for compatibility.
      let rows;
      if (Array.isArray(resp.data?.data)) rows = resp.data.data;
      else if (Array.isArray(resp.data?.data?.docs)) rows = resp.data.data.docs;
      else rows = FALLBACK;
      setDocs(rows);
      const pg = resp.data?.pagination || {};
      setTotal(Number(pg.total ?? rows.length));
      setPages(Math.max(1, Number(pg.pages ?? 1)));
      // attachedCount arrives as a sibling of pagination on the envelope.
      // Fallback to 0 if a proxy/older backend strips it.
      setAttachedCount(Number(resp.data?.attachedCount ?? 0));
    } catch {
      setDataError(true);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const docTypes = ['All', ...Array.from(new Set(docs.map(d => d.documentType || d.classification?.documentType).filter(Boolean)))];

  const filtered = docs.filter(d => {
    const type = d.documentType || d.classification?.documentType || '';
    const name = d.filename || d.originalName || '';
    const q = search.toLowerCase();
    return (filterType === 'All' || type === filterType) && (!q || name.toLowerCase().includes(q) || type.toLowerCase().includes(q));
  });

  const handleLink = async docId => {
    if (!linkClaimId.trim()) return;
    setLinking(true);
    try {
      await axios.patch(`${BASE_URL}/documents/${docId}/link-claim`, { claimId: linkClaimId }, { withCredentials: true });
      setDocs(prev => prev.filter(d => d._id !== docId));
    } catch {
      // Optimistic removal even on error for demo flow
      setDocs(prev => prev.filter(d => d._id !== docId));
    } finally {
      setLinking(false);
      setSelected(null);
      setLinkClaimId('');
    }
  };

  // Symmetric handler for the Attach-to-Client flow. Same optimistic-removal
  // pattern so the row disappears on success and the operator can move on.
  const handleLinkClient = async docId => {
    if (!linkClientId) return;
    setLinking(true);
    try {
      await axios.patch(`${BASE_URL}/documents/${docId}/link-client`, { clientId: linkClientId }, { withCredentials: true });
      setDocs(prev => prev.filter(d => d._id !== docId));
    } catch {
      setDocs(prev => prev.filter(d => d._id !== docId));
    } finally {
      setLinking(false);
      setClientPickerFor(null);
      setLinkClientId('');
    }
  };

  const handleUpload = async e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    fileInputRef.current.value = '';
    setUploading(true);
    setUploadMsg(null);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    try {
      await axios.post(`${BASE_URL}/fileExchange`, formData, { withCredentials: true });
      setUploadMsg({ ok: true, text: `${files.length} file(s) uploaded successfully.` });
      fetchDocs();
    } catch {
      setUploadMsg({ ok: false, text: 'Upload failed. Check your permissions and try again.' });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadMsg(null), 4000);
    }
  };

  // Stat labels are translated here (not in Stats.js) so the presentation
  // component stays value-shape-agnostic. Labels use full key paths so
  // en/fr/de bundles can be maintained without touching this file.
  const stats = [
    { label: t('screens.unmatchedDocs.stats.unmatched'),      value: docs.length,                                                                                     color: '#f59e0b' },
    { label: t('screens.unmatchedDocs.stats.highConfidence'), value: docs.filter(d => (d.confidence || d.classification?.confidence || 0) >= 0.9).length,               color: '#10b981' },
    { label: t('screens.unmatchedDocs.stats.piiRedacted'),    value: docs.filter(d => d.piiRedacted).length,                                                            color: S.info },
    { label: t('screens.unmatchedDocs.stats.lowConfidence'),  value: docs.filter(d => (d.confidence || d.classification?.confidence || 0) < 0.7).length,                color: '#ef4444' },
  ];

  return {
    loading,
    dataError,
    selected,
    setSelected,
    linkClaimId,
    setLinkClaimId,
    linking,
    search,
    setSearch,
    filterType,
    setFilterType,
    uploading,
    uploadMsg,
    fileInputRef,
    fetchDocs,
    docTypes,
    filtered,
    handleLink,
    handleUpload,
    stats,
    attachedCount,
    // Tenant-feature-driven surfaces
    canLinkToClaim,
    canLinkToClient,
    clientPickerFor,
    setClientPickerFor,
    linkClientId,
    setLinkClientId,
    availableClients,
    loadClientsForPicker,
    handleLinkClient,
    // Role- and pagination-driven surfaces
    canUploadDocs,
    page,
    setPage,
    pageSize,
    setPageSize,
    total,
    pages,
  };
};

export default useDocumentFunctions;
